import React from 'react';
import { ReactComponent as File } from 'assets/icons/file.svg';
import { ReactComponent as Remove } from 'assets/icons/close.svg';
import { FieldValues, Path } from 'react-hook-form';
import { CustomFieldProps } from 'components/stepper/field-components/QuestionnaireField';
import {
  FormTextarea,
  FormFileDropzone,
} from 'components/stepper/field-components';
import { useAdditionalInformationHelpers } from './useAdditionalInformationHelpers';
import FieldLabel from 'components/stepper/field-components/FieldLabel';
import {
  formatFileSize,
  mapFileTypeToApiFileType,
} from 'utilities/fileHelpers';

const AdditionalInformation = <T extends FieldValues>({
  question,
}: CustomFieldProps & { isDisabled?: boolean }) => {
  const idStr = String(question.id) as Path<T>;

  const {
    types,
    allowedTypes,
    MAX_FILE_SIZE,
    MAX_FILES,
    files,
    uploadPhoto,
    onChange,
    removeFile,
    isUploading,
  } = useAdditionalInformationHelpers(idStr);
  return (
    <div className="flex flex-col w-[calc(100%_+_306px)] px-[153px] -ml-[153px] pt-30 gap-2.5 border-t border-gray-opacity-15">
      <p className="text-h5 font-semibold text-neutral-800">{question.text}</p>
      <p className="text-sm font-medium text-med-gray-3">
        {question.helper_text}
      </p>
      <FormTextarea
        name={`${idStr}_text`}
        label="Details"
        className="min-h-[116px] w-full overflow-y-auto scrollbar-thin scrollbar-thumb-rounded-full scrollbar-track-rounded-full"
        maxLength={1_000}
        maxRows={8}
        withLength
        placeholder={'Enter details'}
      />
      <div className="flex flex-col gap-1.5">
        <FieldLabel
          name={`${idStr}_file`}
          label={
            <p className="flex gap-2.5 justify-between items-center">
              <span className="font-semibold text-neutral-800">
                Upload additional documents
              </span>
              <span className="text-xs font-semibold text-neutral-800">
                {MAX_FILES - files?.length} files left
              </span>
            </p>
          }
        />
        <FormFileDropzone
          name={`${idStr}_file`}
          supportText={`Supported formats: ${types.join(
            ', ',
          )} \nMaximum file size: ${MAX_FILE_SIZE} MB per file`}
          btnText={'Choose file'}
          className="min-h-[120px]"
          isLoading={isUploading}
          onUpload={uploadPhoto}
          onChange={onChange}
          disabledAside={
            files?.length >= MAX_FILES && (
              <p className="bg-base-content rounded-10 mt-2.5 text-sm font-semibold text-med-gray-3 py-1.5 px-2 text-center w-full">
                You can add only {MAX_FILES} files. To replace a file, delete it
                and add a new one.
              </p>
            )
          }
          disabled={files?.length >= MAX_FILES}
          accept={allowedTypes}
        />
      </div>
      <div className="grid desktop:grid-cols-3 grid-cols-1 gap-2.5">
        {files?.map((file) => (
          <div
            key={file?.fileId}
            className="w-full border border-gray-opacity-15 rounded-5 flex items-center gap-2.5 p-2.5"
          >
            <File className="w-5 h-5 min-w-[20px] fill-med-gray-3" />
            <div className="flex flex-col flex-1 min-w-0">
              <p className="font-semibold text-neutral-800 truncate">
                {file.fileName}
              </p>
              <p className="font-semibold text-med-gray-3 text-sm">
                {mapFileTypeToApiFileType[
                  file.contentType as string
                ]?.toLowerCase()}{' '}
                • {formatFileSize(Number(file.size) || 0)}
              </p>
            </div>
            <Remove
              onClick={() => removeFile(file?.fileId as string)}
              className="w-5 h-5 min-w-[20px] cursor-pointer stroke-med-gray-3"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdditionalInformation;
